import { useEffect, type RefObject } from 'react';

/**
 * Chrome fills a saved password *into the DOM* without dispatching an input event, so a
 * controlled React form never learns about it. The visible symptom is a form that looks
 * filled with a submit button that stays disabled — the user has nothing to click and no
 * way to know why.
 *
 * Two signals cover it: the `-webkit-autofill` animation Chrome runs on the input (see
 * Field.module.css), and a short poll for the case where autofill lands before the
 * listener is attached.
 */
export function useAutofillSync(
  formRef: RefObject<HTMLFormElement | null>,
  onDetect: (values: Record<string, string>) => void,
): void {
  useEffect(() => {
    const form = formRef.current;
    if (!form) return;

    const collect = () => {
      const values: Record<string, string> = {};
      for (const el of form.querySelectorAll('input')) {
        if (el.name && el.value) values[el.name] = el.value;
      }
      if (Object.keys(values).length > 0) onDetect(values);
    };

    const onAnimationStart = (e: AnimationEvent) => {
      if (e.animationName.includes('onAutoFillStart')) collect();
    };

    form.addEventListener('animationstart', onAnimationStart, true);

    // Autofill can land before this effect runs; check once the frame settles.
    const timer = window.setTimeout(collect, 120);

    return () => {
      form.removeEventListener('animationstart', onAnimationStart, true);
      window.clearTimeout(timer);
    };
    // onDetect is expected to be stable (useCallback); re-subscribing on every render
    // would tear down the listener between Chrome's animation start and our read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formRef]);
}
